import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GlitchBorderCard from './GlitchBorderCard';
import PosterSlot from './PosterSlot';
import useMediaQuery from '../utils/useMediaQuery';
import { COLORS, gutter } from './wall/wallTokens';
import { CtaButton, EventShareSheet, eventCta, cardImage, cardFocal } from './wall/WallEventCard';
import CoverActions from './wall/CoverActions';
import WallCardSkeleton from './wall/WallCardSkeleton';
import usePosterReveal from './wall/usePosterReveal';
import { eventPath } from '../utils/eventPath';
import { trackEvent } from '../services/analytics';

/**
 * FEATURED — the curated rail on the events wall (events-wall handoff §4).
 *
 * The cyan/magenta glitch frame stays (owner rule, DECISIONS D-1); the black border, the pink
 * cap strip and the dividers inside the card are gone. One CTA — PLEDGE → or TICKETS → — and a
 * share square on the image. BROADCAST AS SIGNAL left the card.
 *
 * The card shape follows the poster, read when it loads:
 *   desktop portrait (< .95)  a 500-tall row, image 500 × clamp(r, .66, 1) wide, body 290
 *   desktop landscape         440 wide, image on top at 440 / clamp(r, 1.5, 2), cover
 *   mobile (< 980)            every card the same: min(viewport − 56, 380) wide, image
 *                             width × .8, the poster contained over a blurred copy of itself
 *
 * The rail snaps card by card. It no longer drifts on its own — a creeping rail and mandatory
 * snap fight each other, and the handoff asks for snap. Desktop keeps the hover arrows.
 */
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const DESKTOP_H = 500;

function formatWhen(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
  return `${date} · ${time}`;
}

/** Card box for a poster ratio at this viewport. Exported for tests. */
export function featuredGeometry(ratio, { mobile, viewport }) {
  const r = ratio || 1.5;
  if (mobile) {
    const w = Math.round(Math.min(viewport - 56, 380));
    const ih = Math.round(w * 0.8);
    return { w, h: ih + 230, dir: 'column', imgW: w, imgH: ih, fit: 'contain', blurFill: true };
  }
  if (r < 0.95) {
    const iw = Math.round(DESKTOP_H * clamp(r, 0.66, 1));
    return { w: iw + 290, h: DESKTOP_H, dir: 'row', imgW: iw, imgH: DESKTOP_H, fit: 'cover', blurFill: false };
  }
  return { w: 440, h: DESKTOP_H, dir: 'column', imgW: 440, imgH: Math.round(440 / clamp(r, 1.5, 2)), fit: 'cover', blurFill: false };
}

function RailArrow({ side, onClick }) {
  const left = side === 'left';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={left ? 'Scroll featured left' : 'Scroll featured right'}
      className={`pointer-events-none absolute top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center bg-white text-black opacity-0 transition-opacity duration-200 hover:bg-black hover:text-white focus:outline-none focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 group-hover:pointer-events-auto group-hover:opacity-100 lg:flex ${left ? 'left-4' : 'right-4'}`}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
        <path d={left ? 'M15 4 L7 12 L15 20' : 'M9 4 L17 12 L9 20'} />
      </svg>
    </button>
  );
}

function FeaturedCard({ event, geo, onRatio, onOpen, onShare, portal = null }) {
  const { priceText } = eventCta(event);
  const image = cardImage(event);
  const poster = usePosterReveal(image, (r) => onRatio(event.id, r), event.posterUnavailable);
  // A sponsor is paid placement ("Presented by"), so it keeps its line.
  const place = [
    [event.locationCity, event.venueName].filter(Boolean).join(' · '),
    event.sponsorName ? `Presented by ${event.sponsorName}` : '',
  ].filter(Boolean).join(' · ').toUpperCase();
  return (
    <div className="wall-card-shell flex-shrink-0" data-ready={poster.ready} aria-busy={!poster.ready || undefined} style={{ scrollSnapAlign: 'start', width: geo.w, height: geo.h }} data-testid="featured-card" data-dir={geo.dir}>
      <WallCardSkeleton width={geo.w} geometry={geo} overlay />
      <div className="wall-card-content h-full" aria-hidden={!poster.ready || undefined} inert={!poster.ready}>
      <GlitchBorderCard
        offset={5}
        bordered={false}
        onClick={onOpen}
        className="h-full"
        bodyClassName="flex h-full overflow-hidden"
        style={{ height: '100%' }}
      >
        <div className="flex h-full w-full" style={{ flexDirection: geo.dir }}>
          <div className="relative flex-shrink-0" style={{ width: geo.imgW, height: geo.imgH, background: '#0F0F0F' }}>
            {image && !poster.failed ? (
              <>
                {geo.blurFill && (
                  // overflow-hidden so the blur can't bleed past the image area.
                  <div aria-hidden className="absolute inset-0 overflow-hidden">
                    <img
                      src={image}
                      referrerPolicy="no-referrer"
                      alt=""
                      className="absolute block object-cover"
                      style={{ inset: -40, width: 'calc(100% + 80px)', height: 'calc(100% + 80px)', filter: 'blur(30px) brightness(.85)' }}
                    />
                  </div>
                )}
                <img
                  src={image}
                  ref={poster.imageRef}
                  referrerPolicy="no-referrer"
                  alt=""
                  onLoad={poster.onLoad}
                  onError={poster.onError}
                  className="absolute inset-0 block h-full w-full"
                  style={{ objectFit: geo.fit, objectPosition: cardFocal(event) }}
                />
              </>
            ) : (
              <PosterSlot posterUrl={null} alt={event.title} fallbackKey={event.id || event.inviteCode || event.title} className="absolute inset-0" />
            )}
            <span className="absolute left-0 top-0 font-mono text-[10px] font-bold tracking-[0.2em] text-black" style={{ padding: '7px 11px', background: COLORS.featured }}>
              FEATURED
            </span>
            <CoverActions event={event} portal={portal} onShare={onShare} />
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2.5" style={{ padding: '20px 22px 22px' }}>
            <span className="font-mono text-[12px] font-semibold tracking-[0.16em] text-black">{formatWhen(event.dateTime)}</span>
            <h3
              className="m-0 overflow-hidden font-mono font-medium normal-case tracking-normal text-black"
              style={{ fontSize: 22, lineHeight: 1.2, textWrap: 'pretty', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
            >
              {event.title}
            </h3>
            {place && <span className="font-mono text-[11px] tracking-[0.14em]" style={{ color: COLORS.muted }}>{place}</span>}
            <div className="mt-auto flex items-center justify-between gap-3 pt-3">
              <span className="font-mono text-[18px] font-bold uppercase text-black">{priceText}</span>
              <CtaButton event={event} onOpen={onOpen} />
            </div>
          </div>
        </div>
      </GlitchBorderCard>
      </div>
    </div>
  );
}

export default function FeaturedStrip({ events = [], onEventOpen = null, portalFor = null }) {
  const navigate = useNavigate();
  const mobile = useMediaQuery('(max-width: 979px)');
  const phone = useMediaQuery('(max-width: 639px)');
  const pad = gutter(phone);
  const [viewport, setViewport] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    const on = () => setViewport(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const [ratios, setRatios] = useState({});
  const onRatio = useCallback((id, r) => setRatios((prev) => (prev[id] === r ? prev : { ...prev, [id]: r })), []);
  const [shareEvent, setShareEvent] = useState(null);
  const [shareFrom, setShareFrom] = useState(null);
  const railRef = useRef(null);

  if (events.length === 0) return null;

  // One `featured_click` per tap, with the event id in the properties blob — this is the
  // only record that a featured pick was opened FROM the wall, and it is what the admin
  // panel's FEATURED table counts. Fire-and-forget; it never blocks the navigation.
  const open = (e) => {
    trackEvent('featured_click', { eventId: e.id, category: e.category || null });
    if (onEventOpen) onEventOpen(e); else navigate(eventPath(e));
  };
  const nudge = (dir) => {
    const el = railRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <section className="pt-4" aria-label="Featured">
      <div className="mx-auto flex max-w-[1280px] items-center gap-4" style={{ padding: `0 ${pad}px` }}>
        {/* Straight on the page background (lasers): page ink, not black. */}
        <span className="pr-page-ink font-mono text-[13px] font-bold tracking-[0.16em]">FEATURED</span>
        {!phone && <span className="pr-page-sub whitespace-nowrap font-mono text-[11px] tracking-[0.1em]">PICKED FOR YOUR LOCATION</span>}
        <span className="pr-page-rule h-0.5 flex-1" />
        <button
          type="button"
          onClick={() => navigate('/events/all?view=featured')}
          className="pr-page-ink whitespace-nowrap font-mono text-[11px] tracking-[0.1em] underline hover:no-underline"
        >
          SEE ALL {events.length}
        </button>
      </div>

      <div className="group relative">
        <div
          ref={railRef}
          className="flex items-start overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{
            gap: 28,
            padding: `22px ${pad}px 18px`,
            scrollPadding: `0 ${pad}px`,
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
          }}
          data-testid="featured-rail"
        >
          {events.map((e) => (
            <FeaturedCard
              key={e.id}
              event={e}
              geo={featuredGeometry(ratios[e.id] || e.posterAspectRatio, { mobile, viewport })}
              onRatio={onRatio}
              onOpen={() => open(e)}
              onShare={(rect) => { setShareFrom(rect); setShareEvent(e); }}
              portal={portalFor ? portalFor(e.id) : null}
            />
          ))}
        </div>
        {events.length > 1 && (
          <>
            <RailArrow side="left" onClick={() => nudge(-1)} />
            <RailArrow side="right" onClick={() => nudge(1)} />
          </>
        )}
      </div>

      <EventShareSheet event={shareEvent} open={!!shareEvent} originRect={shareFrom} onClose={() => setShareEvent(null)} />
    </section>
  );
}
